import type { ResponseMetric } from "../components/MetricDetailSheet";

export type MetricFactsheet = {
  key: string;
  metric: ResponseMetric;
  keywords: string;
};

// Fichier de démo/factsheet — reflète l'équivalent EN (metricFactsheets.ts).
// Les valeurs numériques et la structure des données sont identiques ;
// seul le texte user-facing est traduit.

export const FR_METRIC_FACTSHEETS: MetricFactsheet[] = [
  {
    key: "estradiol",
    keywords:
      "e2 œstrogène stimulation prise de sang hormone réponse ovarienne",
    metric: {
      label: "Œstradiol",
      current: "1280",
      unit: "pg/mL",
      previous: "840",
      trend: "up",
      context: "En hausse",
      contextGood: true,
      cycleLink: { label: "Cycle actuel" },
      description:
        "L'œstradiol est une hormone œstrogénique produite par les follicules en croissance pendant la stimulation ovarienne.",
      whyItMatters:
        "Ta clinique lit les tendances d'œstradiol avec l'échographie pour suivre l'activité folliculaire et ajuster la stimulation en sécurité.",
      normalRange:
        "Il n'y a pas de cible unique. Pendant la stimulation, l'œstradiol monte en général avec la croissance des follicules ; des taux très élevés peuvent conduire à ajuster le protocole.",
      history: [
        { day: "Jour 5", date: "4 juin", value: "410" },
        { day: "Jour 8", date: "7 juin", value: "840" },
        { day: "Jour 10", date: "9 juin", value: "1280" },
      ],
      valueMode: "cycle",
      chartSeries: [
        {
          label: "Cycle actuel",
          cycleLink: { label: "Cycle actuel" },
          points: [
            { label: "J5", sublabel: "4 juin", value: 410 },
            { label: "J8", sublabel: "7 juin", value: 840 },
            { label: "J10", sublabel: "9 juin", value: 1280 },
          ],
        },
        {
          label: "Cycle FIV n°2",
          cycleLink: { label: "Cycle FIV n°2", isPast: true },
          color: "#9A8E85",
          points: [
            { label: "J5", sublabel: "3 mars", value: 360 },
            { label: "J8", sublabel: "6 mars", value: 790 },
            { label: "J10", sublabel: "8 mars", value: 1110 },
          ],
        },
      ],
    },
  },
  {
    key: "follicles",
    keywords:
      "follicules taille nombre échographie monitoring cfa réponse ponction",
    metric: {
      label: "Follicules",
      imageSource: require("../../assets/images/follicles-ovary.png"),
      current: "12",
      unit: "au total",
      previous: "8",
      trend: "up",
      context: "Bonne réponse",
      contextGood: true,
      cycleLink: { label: "Cycle actuel" },
      description:
        "Le comptage des follicules suit combien de follicules mesurables sont visibles à l'échographie pendant la stimulation.",
      whyItMatters:
        "Le nombre et le profil de tailles aident ta clinique à caler le monitoring, les ajustements de traitement et le déclenchement.",
      normalRange:
        "Les comptages attendus varient selon l'âge, la réserve et le protocole. L'équipe lit le comptage avec les tailles folliculaires et les prises de sang.",
      history: [
        { day: "Jour 5", date: "4 juin", value: "5" },
        { day: "Jour 8", date: "7 juin", value: "8" },
        { day: "Jour 10", date: "9 juin", value: "12" },
      ],
      valueMode: "cycle",
      chartSeries: [
        {
          label: "Cycle actuel",
          cycleLink: { label: "Cycle actuel" },
          points: [
            { label: "J5", sublabel: "4 juin", value: 5 },
            { label: "J8", sublabel: "7 juin", value: 8 },
            { label: "J10", sublabel: "9 juin", value: 12 },
          ],
        },
        {
          label: "Cycle de préservation n°1",
          cycleLink: { label: "Cycle de préservation n°1", isPast: true },
          color: "#9A8E85",
          points: [
            { label: "J5", sublabel: "2 févr.", value: 4 },
            { label: "J8", sublabel: "5 févr.", value: 7 },
            { label: "J10", sublabel: "7 févr.", value: 10 },
          ],
        },
      ],
      relatedDocuments: [
        {
          id: "follicles-us-1",
          title: "Échographie de monitoring",
          subtitle: "Nombre et tailles des follicules",
          date: "9 juin 2026",
        },
        {
          id: "follicles-us-2",
          title: "Échographie du cycle précédent",
          subtitle: "Monitoring — préservation ovocytaire",
          date: "7 févr. 2026",
        },
      ],
    },
  },
  {
    key: "lining",
    keywords:
      "endomètre muqueuse utérine épaisseur trilaminaire échographie transfert",
    metric: {
      label: "Endomètre",
      current: "8,2",
      unit: "mm",
      previous: "6,8",
      trend: "up",
      context: "Optimal",
      contextGood: true,
      cycleLink: { label: "Cycle actuel" },
      description:
        "L'endomètre correspond à l'épaisseur de la muqueuse utérine, mesurée à l'échographie.",
      whyItMatters:
        "Pour un cycle de transfert, l'épaisseur et l'aspect de l'endomètre aident à évaluer si l'utérus est prêt pour le transfert d'embryon.",
      normalRange:
        "Beaucoup de cliniques cherchent au moins 7 mm avant transfert, mais l'aspect, le timing et le protocole comptent aussi.",
      history: [
        { day: "Jour 5", date: "4 juin", value: "5,4" },
        { day: "Jour 8", date: "7 juin", value: "6,8" },
        { day: "Jour 10", date: "9 juin", value: "8,2" },
      ],
      valueMode: "cycle",
    },
  },
  {
    key: "lh",
    keywords: "hormone lutéinisante lh pic ovulation antagoniste monitoring",
    metric: {
      label: "LH",
      current: "3,8",
      unit: "mIU/mL",
      previous: "4,2",
      trend: "down",
      context: "Bien freinée",
      contextGood: true,
      cycleLink: { label: "Cycle actuel" },
      description:
        "La LH aide à déclencher l'ovulation naturellement ; elle est surveillée pendant certains cycles de traitement.",
      whyItMatters:
        "Un pic de LH prématuré peut signifier que l'ovulation démarre avant la ponction ou avant une étape prévue du protocole.",
      normalRange:
        "Les cibles dépendent du protocole. En cycle antagoniste, les cliniques cherchent souvent à garder la LH freinée jusqu'au déclenchement.",
      history: [
        { day: "Jour 5", date: "4 juin", value: "4,8" },
        { day: "Jour 8", date: "7 juin", value: "4,2" },
        { day: "Jour 10", date: "9 juin", value: "3,8" },
      ],
      valueMode: "cycle",
    },
  },
  {
    key: "progesterone",
    keywords:
      "progestérone p4 lutéale lutéinisation prématurée déclenchement transfert",
    metric: {
      label: "Progestérone",
      current: "0,8",
      unit: "ng/mL",
      previous: "0,6",
      trend: "up",
      context: "Dans les clous",
      contextGood: true,
      cycleLink: { label: "Cycle actuel" },
      description:
        "La progestérone aide à préparer l'endomètre après l'ovulation, mais le timing est décisif en stimulation et en cycle de transfert.",
      whyItMatters:
        "Une montée trop précoce en stimulation peut affecter la ponction ou le transfert, d'où le suivi près du déclenchement.",
      normalRange:
        "En protocole antagoniste, on attend souvent que la progestérone reste basse jusqu'après le déclenchement.",
      history: [
        { day: "Jour 5", date: "4 juin", value: "0,4" },
        { day: "Jour 8", date: "7 juin", value: "0,6" },
        { day: "Jour 10", date: "9 juin", value: "0,8" },
      ],
      valueMode: "cycle",
    },
  },
  {
    key: "vitamin-d",
    keywords: "vitamine d 25-oh nutriment carence supplémentation biomarqueur",
    metric: {
      label: "Vitamine D",
      imageSource: require("../../assets/images/vitamin-d-icon.png"),
      current: "34",
      unit: "ng/mL",
      previous: "24",
      trend: "up",
      context: "Améliorée",
      contextGood: true,
      description:
        "La vitamine D est une vitamine liposoluble qui soutient la santé osseuse, la fonction immunitaire et plusieurs voies hormonales reproductives.",
      whyItMatters:
        "Une vitamine D basse est fréquente et se corrige dans le temps. Ton clinicien peut la suivre avec d'autres marqueurs nutritionnels, thyroïdiens et métaboliques.",
      normalRange:
        "Beaucoup de laboratoires considèrent 30 à 50 ng/mL comme suffisant, mais les cibles varient selon la clinique, les antécédents et le plan de supplémentation.",
      history: [
        { day: "janv.", date: "2025", value: "18" },
        { day: "mai", date: "2025", value: "24" },
        { day: "nov.", date: "2025", value: "31" },
        { day: "juin", date: "2026", value: "34" },
      ],
      valueMode: "lifetime",
      chartSeries: [
        {
          label: "Tendance à long terme",
          points: [
            { label: "janv. 25", value: 18 },
            { label: "mai 25", value: 24 },
            { label: "nov. 25", value: 31 },
            { label: "juin 26", value: 34 },
          ],
        },
      ],
      relatedDocuments: [
        {
          id: "vitd-lab-1",
          title: "Bilan biomarqueurs complet",
          subtitle: "Vitamine D, thyroïde, marqueurs métaboliques",
          date: "12 juin 2026",
        },
      ],
    },
  },
  {
    key: "amh",
    keywords:
      "amh hormone anti-müllérienne réserve ovarienne marqueur fertilité",
    metric: {
      label: "AMH",
      current: undefined,
      unit: "ng/mL",
      previous: undefined,
      trend: "stable",
      context: undefined,
      contextGood: true,
      description:
        "L'AMH est une hormone produite par les petits follicules ovariens ; elle sert souvent de marqueur de la réserve ovarienne.",
      whyItMatters:
        "L'AMH aide à estimer la réponse attendue à la stimulation, mais elle ne mesure pas directement la qualité ovocytaire ni ne garantit un résultat de fertilité.",
      normalRange:
        "Les valeurs varient selon l'âge et le laboratoire. Ton clinicien interprète l'AMH avec le CFA, l'âge, les antécédents et les objectifs de traitement.",
      history: [],
      valueMode: "lifetime",
      relatedDocuments: [],
    },
  },
];
